import { useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './useAuth';
import { api } from '../services/api';
import { TenantContext } from './useTenant';

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  const [selectedTenantId, setSelectedTenantIdState] = useState<string>('');
  const [isReady, setIsReady] = useState(false);

  /**
   * Load tenant preference from database for SUPERADMIN users
   */
  const loadPreference = useCallback(async () => {
    if (!isAuthenticated) {
      setIsReady(false);
      return;
    }

    if (!isSuperAdmin) {
      // Non-SUPERADMIN users are always ready (they use their assigned tenant)
      setIsReady(true);
      return;
    }

    try {
      const response = await api.get('/auth/preferences/selectedTenantId');
      const tenantId = response.data.data;
      if (tenantId) {
        setSelectedTenantIdState(tenantId);
      }
      setIsReady(true);
    } catch {
      // If preference doesn't exist, still mark as ready
      setIsReady(true);
    }
  }, [isAuthenticated, isSuperAdmin]);

  // Load preference when auth state changes
  useEffect(() => {
    loadPreference();
  }, [loadPreference]);

  /**
   * Save tenant preference to database (SUPERADMIN only)
   */
  const setSelectedTenantId = async (id: string) => {
    if (!isSuperAdmin) {
      return;
    }

    // Save to database
    await api.put('/auth/preferences/selectedTenantId', { value: id });
    setSelectedTenantIdState(id);
    setIsReady(true);
  };

  return (
    <TenantContext.Provider value={{ selectedTenantId, setSelectedTenantId, isReady }}>
      {children}
    </TenantContext.Provider>
  );
}
